import { useState, useEffect } from 'react';
import axios from 'axios';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import AdminSendEmailConfirmModal from '../../modals/AdminSendEmailConfirmModal';
import { Divider } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Select from '@mui/material/Select';
import MuiSnackbar from '../../common/MuiSnackbar';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';

const SendEmail = () => {
  // Count of filtered users state
  const [userCount, setUserCount] = useState();

  //User filter states
  const [roles, setRoles] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [emailVerifieds, setEmailVerifieds] = useState([]);
  const [giveAways, setGiveAways] = useState([]);

  //Template states
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');

  //Modal open/close state
  const [modalOpen, setModalOpen] = useState(false);

  //DB query loading state
  const [loading, setLoading] = useState(false);

  //Email summary states
  const [total, setTotal] = useState();
  const [successCount, setSuccessCount] = useState();
  const [failCount, setFailCount] = useState();

  //Snackbar states
  const [open, setOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [severity, setSeverity] = useState('success');

  const getFilteredUsers = async () => {
    setLoading(true);
    setUserCount(null);
    try {
      const response = await axios({
        method: 'POST',
        url: '/api/v1/admin/getFilteredUsers',
        data: { roles, statuses, emailVerifieds, giveAways },
      });

      setUserCount(response.data.data.users.length);
    } catch (err) {
      console.log('Error->', err.response.data.message);
    }
    setLoading(false);
  };

  const sendCustomEmail = async () => {
    setLoading(true);
    try {
      const response = await axios({
        method: 'POST',
        url: '/api/v1/admin/sendCustomEmail',
        data: { roles, statuses, emailVerifieds, giveAways, templateName },
      });
      setOpen(true);
      setAlertMessage(response.data.message);
      setTotal(response.data.total);
      setSuccessCount(response.data.successCount);
      setFailCount(response.data.failCount);
      setSeverity('success');
    } catch (err) {
      setOpen(true);
      setAlertMessage(err.response.data.message);
      setSeverity('error');
    }
    setLoading(false);
  };

  //User filter change handlers
  const handleChangeRoles = (event) => {
    setRoles(event.target.value);
  };
  const handleChangeStatuses = (event) => {
    setStatuses(event.target.value);
  };
  const handleChangeEmailVerifieds = (event) => {
    setEmailVerifieds(event.target.value);
  };
  const handleChangeGiveAways = (event) => {
    setGiveAways(event.target.value);
  };
  const handleChangeTemplateName = (event) => {
    setTemplateName(event.target.value);
  };

  //Clear filters
  const handleClearFilters = () => {
    setRoles([]);
    setStatuses([]);
    setEmailVerifieds([]);
    setGiveAways([]);
  };

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const response = await axios.get('/api/v1/admin/getCustomEmailTemplates');
        const list = response.data?.data?.templates || [];
        setTemplates(list);
        if (list.length) {
          setTemplateName((current) => current || '');
        }
      } catch (err) {
        console.log('Error->', err.response?.data?.message || err.message);
      }
    };

    loadTemplates();
  }, []);

  //User filter select values
  const roleValues = ['basic', 'standard', 'premium', 'professional', 'support', 'admin'];
  const statusValues = ['active', 'passive'];
  const emailVerifiedValues = ['yes', 'no'];
  const giveAwayValues = ['yes', 'no'];

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 5;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 200,
      },
    },
  };

  return (
    <div className="adminDashboard">
      <MuiSnackbar open={open} setOpen={setOpen} severity={severity} alertMessage={alertMessage} />

      <AdminSendEmailConfirmModal
        open={modalOpen}
        setOpen={setModalOpen}
        queryFunction={sendCustomEmail}
        roles={roles}
        statuses={statuses}
        emailVerifieds={emailVerifieds}
        giveAways={giveAways}
        templateName={templateName}
        userCount={userCount}
        loading={loading}
      />

      <h1>Select Users</h1>
      <div className="adminSendEmailUserFilterContainer">
        <FormControl sx={{ width: 150 }}>
          <InputLabel id="demo-multiple-checkbox-label">Role</InputLabel>
          <Select
            labelId="demo-multiple-checkbox-label"
            id="demo-multiple-checkbox"
            multiple
            value={roles}
            onChange={handleChangeRoles}
            input={<OutlinedInput label="Roles" />}
            renderValue={(selected) => selected.join(', ')}
            MenuProps={MenuProps}
          >
            {roleValues.map((role) => (
              <MenuItem key={role} value={role}>
                <Checkbox checked={roles.includes(role)} />
                <ListItemText primary={role} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ width: 150 }}>
          <InputLabel id="demo-multiple-checkbox-label">Status</InputLabel>
          <Select
            labelId="demo-multiple-checkbox-label"
            id="demo-multiple-checkbox"
            multiple
            value={statuses}
            onChange={handleChangeStatuses}
            input={<OutlinedInput label="Status" />}
            renderValue={(selected) => selected.join(', ')}
            MenuProps={MenuProps}
          >
            {statusValues.map((status) => (
              <MenuItem key={status} value={status}>
                <Checkbox checked={statuses.includes(status)} />
                <ListItemText primary={status} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ width: 150 }}>
          <InputLabel id="demo-multiple-checkbox-label">Email Verified</InputLabel>
          <Select
            labelId="demo-multiple-checkbox-label"
            id="demo-multiple-checkbox"
            multiple
            value={emailVerifieds}
            onChange={handleChangeEmailVerifieds}
            input={<OutlinedInput label="Email Verified" />}
            renderValue={(selected) => selected.join(', ')}
            MenuProps={MenuProps}
          >
            {emailVerifiedValues.map((emailVerified) => (
              <MenuItem key={emailVerified} value={emailVerified}>
                <Checkbox checked={emailVerifieds.includes(emailVerified)} />
                <ListItemText primary={emailVerified} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ width: 150 }}>
          <InputLabel id="demo-multiple-checkbox-label">Give Away</InputLabel>
          <Select
            labelId="demo-multiple-checkbox-label"
            id="demo-multiple-checkbox"
            multiple
            value={giveAways}
            onChange={handleChangeGiveAways}
            input={<OutlinedInput label="Give Away" />}
            renderValue={(selected) => selected.join(', ')}
            MenuProps={MenuProps}
          >
            {giveAwayValues.map((giveAway) => (
              <MenuItem key={giveAway} value={giveAway}>
                <Checkbox checked={giveAways.includes(giveAway)} />
                <ListItemText primary={giveAway} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ width: 150 }}>
          <Button sx={{ m: 1, width: 150 }} variant="outlined" onClick={handleClearFilters}>
            Clear Filters
          </Button>
        </FormControl>
      </div>
      <h1>Send Custom Email</h1>
      <div className="adminSendEmail">
        <FormControl sx={{ width: 250 }}>
          <InputLabel id="admin-template-select-label">Template</InputLabel>
          <Select
            labelId="admin-template-select-label"
            id="admin-template-select"
            value={templateName}
            onChange={handleChangeTemplateName}
            input={<OutlinedInput label="Template" />}
          >
            <MenuItem value="" disabled>
              Select template
            </MenuItem>
            {templates.length ? (
              templates.map((template) => (
                <MenuItem key={template} value={template}>
                  {template}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="" disabled>
                No templates available
              </MenuItem>
            )}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={() => {
            setModalOpen(true);
            getFilteredUsers();
          }}
          disabled={loading || !templateName}
        >
          {loading ? 'Sending...' : 'Confirm Email Details'}
        </Button>
      </div>
      <Divider sx={{ marginTop: '20px', marginBottom: '10px' }} />
      <h3>Email Summary</h3>
      <div className="mailSummary">
        <span>Total: {loading ? <CircularProgress size={15} /> : total ? total : '-'}</span>
        <span>Success: {loading ? <CircularProgress size={15} /> : successCount ? successCount : '-'}</span>
        <span>Fail: {loading ? <CircularProgress size={15} /> : failCount ? failCount : '-'}</span>
      </div>
    </div>
  );
};

export default SendEmail;
